import { ApolloLink } from '@apollo/client';
import { getCurrentToken } from '@teamexos/fit-shared';
import type { ClientOptions } from '@teamexos/prince-sdk';
import { PrinceSdkProvider } from '@teamexos/prince-sdk';
import { sha256 } from 'crypto-hash';
import React, { useMemo } from 'react';
import { datadogGraphqlErrorLink } from 'utils/datadogGraphqlErrorLink';
import { getTimezone } from 'utils/dates';
import typePolicies from '../apollo/typePolicies';
import fragments from '../graphql/fragments.json';

const version = process.env.REACT_APP_VERSION ?? 'unknown';
const { possibleTypes } = fragments;

/**
 * Add custom headers that will be sent to the Shared Platform
 */
const addHeadersExtraLink = new ApolloLink((operation, forward) => {
  const headers = {
    'x-application-version': `web/${version}`,
    'x-application-name': 'fit-web',
    'x-timezone': getTimezone(),
  };
  operation.setContext({
    headers,
  });

  return forward(operation);
});

const apolloExtraLink = datadogGraphqlErrorLink.concat(addHeadersExtraLink);

const PrinceSDKProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const clientOptions: ClientOptions = useMemo(
    () => ({
      getToken: async () => {
        const tokens = await getCurrentToken();

        return tokens?.idToken?.toString();
      },
      clientUrl: process.env.REACT_APP_PRINCE_GQL_URL,
      wsUrl: process.env.REACT_APP_PRINCE_WS_URL,
      possibleTypes,
      apolloClientOptions: {
        clientAwareness: {
          name: 'heart-web',
          version,
        },
        // Replicates the SDK defaults (this object replaces them), plus pins
        // notifyOnNetworkStatusChange to the Apollo Client 3 behavior: v4
        // defaults it to true, which flips `loading` during fetchMore and
        // would swap paginated lists back to skeletons mid-scroll.
        defaultOptions: {
          query: {
            fetchPolicy: 'cache-first',
          },
          watchQuery: {
            fetchPolicy: 'cache-and-network',
            nextFetchPolicy: 'cache-first',
            notifyOnNetworkStatusChange: false,
          },
        },
      },
      debug: process.env.NODE_ENV === 'development',
      extraLink: apolloExtraLink,
      typePolicies,
      sha256,
    }),
    [],
  );

  return (
    <PrinceSdkProvider clientOptions={clientOptions}>
      {children}
    </PrinceSdkProvider>
  );
};

export default PrinceSDKProvider;
